/**
 *
 * @module bamIdentityApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/identity-bam.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/BamIdentityApi.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Services from './../../services/entry';
import BamIdentityManager from './bamIdentityManager';

const Util = Services.Util;
const Identity = Services.Identity.Bam.Identity;
const OneTimePasscodeRequestReason = Services.Identity.Bam.OneTimePasscodeRequestReason;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const BamIdentityApiDustUrnReference = Services.Internal.Dust.DustUrnReference.identity.bam.bamIdentityApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    string,
    nonEmptyObject,
    object,
    instanceStrict,
    nonEmptyString,
    includes
} = Check.assert;

/**
 *
 * @class BamIdentityApi
 * @desc Provides access to the BAMTECH identity services.
 *
 */
export default class BamIdentityApi {

    /**
     *
     * @param {BamIdentityManager} bamIdentityManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(bamIdentityManager, logger = Logging) {

        const errorMessage = {
            ctor: `${this.constructor.name}(bamIdentityManager, logger)`,
            get bamIdentityManagerError() {
                return `${this.ctor} needs BamIdentityManager instance`;
            }
        };

        instanceStrict(bamIdentityManager, BamIdentityManager, errorMessage.bamIdentityManager, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {BamIdentityManager}
         *
         */
        this.bamIdentityManager = bamIdentityManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.bamIdentityManager.client.dustEnabled);

        this.logger.info(BamIdentityApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @desc Indicates that authentication has not been performed recently, a requirement for some methods.
     * @returns {Boolean}
     *
     */
    requiresAuthentication() {

        const { bamIdentityManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.requiresAuthentication
        });

        return bamIdentityManager.requiresAuthentication().then((authenticationRequired) => {

            return Promise.resolve(authenticationRequired);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity.
     * @param {String} password - The password associated with the identity.
     * @desc Logs the user in with the BAMTECH identity service.
     * @note Creates Bam identity token, exchanges for an account grant,
     * and exchanges account grant for a new AccessContext.
     * @throws {InvalidCredentialsException} Incorrect email or password.
     * @note Request to get identity token returns HTTP 401 with error code: idp.error.identity.bad-credentials
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<IdentityToken>} A promise that completes when the authenticate operation has succeeded.
     * @note Creates Bam identity token. App developers must exchange for an account grant and exchange account grant
     * for a new AccessContext via the AccountApi.
     *
     */
    authenticate(email, password) {

        const errorMessage = {
            method: `${this.constructor.name}.authenticate(email, password)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.authenticate
        });

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);

        const { bamIdentityManager } = this;

        return bamIdentityManager.authenticate(email, password).then((identityToken) => {

            logger.info(BamIdentityApi.name, 'Authenticate completed successfully.');

            return Promise.resolve(identityToken);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {String} options.email - The email to create a new identity for.
     * @param {String} options.password - The password to create for the new identity.
     * @param {Object} [options.attributes={}]
     * @note {Object} [options.metadata={}] - Contains additional information about the created identity (ex: isTest)
     * indicates if the account being created is for testing purposes and should have a temporary lifespan
     * @desc Creates a Bam Identity with the credentials provided.
     * @note Registers a new Bam identity, obtains an identity token, creates a new account,
     * exchanges for an account grant, and exchanges account grant for a new AccessContext.
     * @throws {InvalidCredentialsException} The email address already exists and the password does not match.
     * @note Request to create new identity returns HTTP 4xx with error code: idp.error.identity.already-exists
     * and a subsequent request to authenticate returns HTTP 4xx with error code: idp.error.identity.bad-credentials
     * @throws {AttributeValidationException} One or more attributes supplied are invalid.
     * @note Request to create new identity returns HTTP 4xx with error code: payload.fields.incorrect
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<IdentityToken>} A promise that completes when the operation has succeeded.
     * @note Creates Bam identity token. App devs must exchange for an account grant and exchange account grant
     * for a new AccessContext via the AccountApi.
     *
     */
    createIdentity(options) {

        const errorMessage = {
            method: `${this.constructor.name}.createIdentity(options)`,
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.createIdentity
        });

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { email, password, attributes = {}, metadata } = options;

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { bamIdentityManager } = this;


        return bamIdentityManager.createIdentity(options).then(() => {

            logger.info(BamIdentityApi.name, 'Created identity.');

            return this.authenticate(email, password);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity that needs to reset the password.
     * @desc Given an email for the user, requests a password reset email.
     * @note Requests a password reset with the bam identity service.
     * The rest of the flow is completed by the user after receiving the email.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    requestPasswordReset(email) {

        const errorMessage = {
            method: `${this.constructor.name}.requestPasswordReset(email)`,
            get email() {
                return `${this.method} email should be a String with length`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.requestPasswordReset
        });

        string(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(email, errorMessage.email, InvalidArgumentException);

        const { bamIdentityManager } = this;

        return bamIdentityManager.requestPasswordReset(email).then(() => {

            logger.info(BamIdentityApi.name, 'Requested password reset.');

            return Promise.resolve();

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} newPassword - The new password to associate with the existing identity.
     * @param {String} resetToken - The reset token provided via email to verify the owner of the account.
     * @desc Following a requestPasswordReset, use this method to update the
     * user's password with the token provided in the password reset email.
     * @note Requests a password update using the token supplied and the new password.
     * @throws {TokenAlreadyUsedException} Unable to use the reset token, it's already been used.
     * @note services returns a 4xx with code: idp.error.reset-token.already-used
     * @throws {TokenInvalidException} Unable to reset the password as the reset token isn't recognized by the service.
     * @note services returns a 4xx with code: idp.error.reset-token.invalid
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    resetPassword(newPassword, resetToken) {

        const errorMessage = {
            method: `${this.constructor.name}.resetPassword(newPassword, resetToken)`,
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get resetToken() {
                return `${this.method} resetToken should be a String with length`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.resetPassword
        });

        string(newPassword, errorMessage.newPassword, InvalidArgumentException);
        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);
        string(resetToken, errorMessage.resetToken, InvalidArgumentException);
        nonEmptyString(resetToken, errorMessage.resetToken, InvalidArgumentException);

        const { bamIdentityManager } = this;

        return bamIdentityManager.resetPassword(newPassword, resetToken).then(() => {

            logger.info(BamIdentityApi.name, 'Reset password successfully.');

            return Promise.resolve();

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} newEmail - The new email to change the identity to use.
     * @param {String} password - The current password for verification purposes.
     * @desc Updates the user's email for the BAM Identity. The current password
     * must be provided for verification. Requires user to have recently authenticated.
     * @note Gets an identity token using the login credentials, issues request to
     * change email using that identity token and the new email.
     * @throws {AuthenticationRequiredException} User has not authenticated recently and must log in again.
     * @note The security token for this operation is either missing, or has expired.
     * @throws {InvalidCredentialsException} Incorrect email or password.
     * @note Request to get identity token returns HTTP 401 with error code: idp.error.identity.bad-credentials
     * @throws {InvalidEmailException} The new email address is invalid.
     * @note Request to update email returns HTTP 4xx with error: idp.error.payload.fields.incorrect.
     * @throws {UpdateIdentityConflictException} The new email address is already taken.
     * @note Request to update email returns HTTP 4xx with error: idp.error.identity.already-exists.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    updateEmail(newEmail, password) {

        const errorMessage = {
            method: `${this.constructor.name}.updateEmail(newEmail, password)`,
            get newEmail() {
                return `${this.method} newEmail should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.updateEmail
        });

        nonEmptyString(newEmail, errorMessage.newEmail, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);

        const { bamIdentityManager } = this;

        return bamIdentityManager.changeEmail(newEmail, password).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The current email for verification purposes.
     * @param {String} newPassword - The new password to associate with the current identity.
     * @param {String} [oldPassword] - The current password for verification purposes.
     * @desc Updates the user's password for the BAM Identity. The current password can
     * be provided for verification in non-OTP flows. Requires user to have recently authenticated.
     * @note Gets an identity token using the login credentials, issues request to
     * change password using that identity token and the new password.
     * @throws {AuthenticationRequiredException} User has not authenticated recently and must log in again.
     * @note The security token for this operation is either missing, or has expired.
     * @throws {InvalidCredentialsException} Incorrect email or password.
     * @note Request to get identity token returns HTTP 401 with error code: idp.error.identity.bad-credentials
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    updatePassword(newPassword, oldPassword) {

        const errorMessage = {
            method: `${this.constructor.name}.updatePassword(newPassword, oldPassword)`,
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get oldPassword() {
                return `${this.method} oldPassword should be a String with length`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.updatePassword
        });

        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);

        if (Check.assigned(oldPassword)) {
            nonEmptyString(oldPassword, errorMessage.oldPassword, InvalidArgumentException);
        }

        const { bamIdentityManager } = this;

        return bamIdentityManager.changePassword(newPassword, oldPassword).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Gets the user's bam identity. Requires user to have recently authenticated.
     * @throws {AuthenticationRequiredException} User has not authenticated recently and must log in again.
     * @note The security token for this operation is either missing, or has expired.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Identity>}
     *
     */
    getIdentity() {

        const { bamIdentityManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.updateEmail
        });

        return bamIdentityManager.getIdentity().then((identity) => {

            logger.info(BamIdentityApi.name, 'Identity retrieved successfully.');

            return Promise.resolve(identity);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Updates the user's bam identity. Requires user to have recently authenticated.
     * @param {Identity} identity - The identity to be updated
     * @throws {AuthenticationRequiredException} User has not authenticated recently and must log in again.
     * @note The security token for this operation is either missing, or has expired.
     * @throws {AttributeUpdateFailedException} The attributes weren't updated due to required field removal
     * or failed validation.
     * @note Request returns a 4xx error with the code:idp.error.attributes.update.failed
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     */
    updateIdentity(identity) {

        const errorMessage = {
            method: `${this.constructor.name}.updateIdentity(identity)`,
            get identity() {
                return `${this.method} needs Identity instance`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.updateIdentity
        });

        instanceStrict(identity, Identity, errorMessage.identity, InvalidArgumentException);

        const { bamIdentityManager } = this;

        return bamIdentityManager.updateAttributes(identity.attributes).then(() => {

            logger.info(BamIdentityApi.name, 'Updated Identity attributes successfully.');

            return Promise.resolve();

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @desc Checks if the Bam Identity Service recognizes this OneId account
     * @param {String} email The email used to determine if an identity exists
     * @note Apps should perform the action returned from this call
     * @throws {InvalidEmailSyntaxException} Invalid email address syntax
     * @note Inform user that the email address wasn't valid and to fix the address and try again
     * @note Unexpected response from server.
     * @note The HTTP code is 400 and the `code` will be `idp.error.payload.fields.incorrect`
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<SDK.Services.Identity.Bam.AuthenticationFlow>>} resolves with a list of actions that will
     * indicate if a user's email address is registered with BamIdp
     *
     */
    async getAuthenticationFlow(email) {

        const errorMessage = {
            method: `${this.constructor.name}.getAuthenticationFlow(email)`,
            get email() {
                return `${this.method} email should be a String with length`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);

        const { bamIdentityManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.getAuthenticationFlow
        });

        try {

            return await bamIdentityManager.getAuthenticationFlow(email);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} email - The email to send the passcode to.
     * @param {SDK.Services.Identity.Bam.OneTimePasscodeRequestReason} reason - The intended use of the passcode.
     * @desc Request that a one-time passcode be sent to an email address.
     * @throws {RateLimitedException} Too many requests.
     * @note Request to get identity token returns HTTP 400 with error code: `idp.error.auth.otp.throttled`
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: `unauthorized_client` and
     * error_description: `unknown-location`, `unreliable-location`, or `forbidden-location`.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     */
    async requestOneTimePasscode(email, reason) {

        const errorMessage = {
            method: `${this.constructor.name}.requestOneTimePasscode(email, reason)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get reason() {
                return `${this.method} reason should be a valid SDK.Services.Identity.Bam.OneTimePasscodeRequestReason`;
            }
        };

        const { bamIdentityManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.requestOneTimePasscode,
            data: {
                reason
            }
        });

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        includes(OneTimePasscodeRequestReason, reason, errorMessage.reason, InvalidArgumentException);
        try {

            return await bamIdentityManager.requestOneTimePasscode(email, reason);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} email - The email that received the passcode.
     * @param {String} passcode - The passcode sent to the email associated with the identity.
     * @desc Logs the user in with the BAM identity service.
     * @throws {InvalidPasscodeException} Invalid passcode.
     * @note Request to get identity token returns HTTP 400 with error code: `idp.error.auth.otp.incorrect`
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: `unauthorized_client` and
     * error_description: `unknown-location`, `unreliable-location`, or `forbidden-location`.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Identity.Bam.RedeemedPasscodeToken>}
     *
     */
    async authenticateWithPasscode(email, passcode) {

        const errorMessage = {
            method: `${this.constructor.name}.authenticateWithPasscode(email, passcode)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get passcode() {
                return `${this.method} passcode should be a String with length`;
            }
        };

        const { bamIdentityManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityApi.name,
            urn: BamIdentityApiDustUrnReference.authenticateWithPasscode
        });

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(passcode, errorMessage.passcode, InvalidArgumentException);

        try {

            return await bamIdentityManager.redeemOneTimePasscode(email, passcode);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

}



// WEBPACK FOOTER //
// ./src/identity/bam/bamIdentityApi.js